import StickyTableResponsive from '../../../components/common/StickyTableResponsive';
import SortableTh from '../../../components/common/SortableTh';
import React, { useCallback, useEffect, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import { Link } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import { AppFilterBar, AppFilterInput } from '../../../components/filters';
import {
  AppButton,
  AppFormField,
  AppInput,
  AppModal,
  AppPagination,
  AppSelect,
  AppSpinner,
} from '../../../components/ui';
import useAppConfirm from '../../../hooks/useAppConfirm';
import secteursAdminService from '../../../services/secteursAdminService';
import { notify } from '../../../components/common/AppToast';
import { ROUTES } from '../../../config/routes';
import { getSocieteBrand } from '../../../config/societeBrand';
import '../../../styles/kkp-planning-page.scss';
const NON_MAPPE_SORT_COLUMNS = {
  id: { type: 'number', getValue: (row) => row.productId ?? 0 },
  ref: { type: 'string', getValue: (row) => row.productRef },
  label: { type: 'string', getValue: (row) => row.productLabel },
  occ: { type: 'number', getValue: (row) => row.occurrences ?? 0 },
};

const MAPPING_SORT_COLUMNS = {
  productId: { type: 'number', getValue: (row) => row.productId ?? 0 },
  ref: { type: 'string', getValue: (row) => row.productRef },
  label: { type: 'string', getValue: (row) => row.productLabel },
  societe: { type: 'string', getValue: (row) => row.societe },
  famille: { type: 'string', getValue: (row) => row.famille },
};

const SOCIETES = ['iticonseil', 'koikispass', 'autre'];

const EMPTY_FORM = { productId: '', productRef: '', productLabel: '', societe: 'iticonseil', famille: '' };

const SecteursAdminProduitsSocietePage = () => {
  const { ask, modal } = useAppConfirm();
  const [produits, setProduits] = useState([]);
  const [produitsNonMappes, setProduitsNonMappes] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const { sortedRows: sortedNonMappes, sort: sortNonMappes, toggleSort: toggleSortNonMappes, getSortIcon: getSortIconNonMappes } = useTableSort(
    produitsNonMappes.slice(0, 10),
    NON_MAPPE_SORT_COLUMNS,
  );
  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(produits, MAPPING_SORT_COLUMNS);

  const load = useCallback(async (page = 1, q = search) => {
    setLoading(true);
    try {
      const result = await secteursAdminService.listProduitsSociete({ search: q, page, limit: 20 });
      setProduits(result.produits);
      setPagination(result.pagination);
      setProduitsNonMappes(result.produitsNonMappes);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => load(1), 300);
    return () => clearTimeout(timer);
  }, [load]);

  const openCreate = (prefill = {}) => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, ...prefill });
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      productId: p.productId ?? '',
      productRef: p.productRef || '',
      productLabel: p.productLabel || '',
      societe: p.societe || 'iticonseil',
      famille: p.famille || '',
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      productId: form.productId ? Number(form.productId) : null,
    };
    try {
      if (editingId) {
        await secteursAdminService.updateProduitSociete(editingId, payload);
        notify.success('Mapping enregistré');
      } else {
        await secteursAdminService.createProduitSociete(payload);
        notify.success('Mapping créé');
      }
      setModalOpen(false);
      load(pagination.page);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (produitId) => {
    const ok = await ask({
      title: 'Supprimer ce mapping ?',
      message: 'Le lien produit / société sera retiré.',
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await secteursAdminService.deleteProduitSociete(produitId);
      notify.success('Mapping supprimé');
      load(pagination.page);
    } catch (err) {
      notify.error(err.message);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Mappings produit → société"
        subtitle="Correspondance produits Dolibarr / société"
        actions={(
          <div className="d-flex gap-2">
            <AppButton as={Link} to={ROUTES.secteurs.admin} size="sm" variant="outline-secondary">Retour</AppButton>
            <AppButton size="sm" icon="mdi:plus" onClick={() => openCreate()}>
              Nouveau mapping
            </AppButton>
          </div>
        )}
      />

      {produitsNonMappes.length > 0 && (
        <div className="card mb-24 border-warning">
          <div className="card-header bg-warning-100">
            <h6 className="mb-0">Suggestions : produits non mappés ({produitsNonMappes.length})</h6>
          </div>
          <div className="card-body p-0">
            <StickyTableResponsive>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <SortableTh sortKey="id" label="ID" sort={sortNonMappes} onSort={toggleSortNonMappes} getSortIcon={getSortIconNonMappes} />
                    <SortableTh sortKey="ref" label="Réf." sort={sortNonMappes} onSort={toggleSortNonMappes} getSortIcon={getSortIconNonMappes} />
                    <SortableTh sortKey="label" label="Label" sort={sortNonMappes} onSort={toggleSortNonMappes} getSortIcon={getSortIconNonMappes} />
                    <SortableTh sortKey="occ" label="Occ." sort={sortNonMappes} onSort={toggleSortNonMappes} getSortIcon={getSortIconNonMappes} />
                    <SortableTh label="" sortable={false} />
                  </tr>
                </thead>
                <tbody>
                  {sortedNonMappes.map((p, i) => (
                    <tr key={i}>
                      <td><code>{p.productId || '—'}</code></td>
                      <td>{p.productRef || '—'}</td>
                      <td>{p.productLabel || '—'}</td>
                      <td>{p.occurrences ?? '—'}</td>
                      <td>
                        <AppButton size="sm" variant="warning" onClick={() => openCreate({
                          productId: p.productId || '',
                          productRef: p.productRef || '',
                          productLabel: p.productLabel || '',
                        })}>
                          Créer
                        </AppButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          </div>
        </div>
      )}

      <AppFilterBar onReset={() => setSearch('')} resetDisabled={!search}>
        <AppFilterInput
          id="admin-produits-societe-search"
          label="Recherche"
          col="col-12 col-md-8 col-lg-6"
          placeholder="Label, référence ou société…"
          value={search}
          onChange={setSearch}
        />
      </AppFilterBar>

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <SortableTh sortKey="productId" label="ID produit" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="ref" label="Réf." sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="label" label="Label" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="societe" label="Société" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="famille" label="Famille" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh label="Actions" sortable={false} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((p) => (
                    <tr key={p.id}>
                      <td><code>{p.productId || '—'}</code></td>
                      <td>{p.productRef || '—'}</td>
                      <td>{p.productLabel || '—'}</td>
                      <td><span className={`badge ${getSocieteBrand(p.societe).badgeClass}`}>{p.societe}</span></td>
                      <td>{p.famille || '—'}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <AppButton size="sm" variant="outline-primary" icon="mdi:pencil" onClick={() => openEdit(p)} />
                          <AppButton size="sm" variant="outline-danger" icon="mdi:delete" onClick={() => handleDelete(p.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>

      <AppPagination
        pagination={pagination}
        search={search}
        onPageChange={(p) => load(p)}
      />

      <AppModal
        show={modalOpen}
        title={editingId ? 'Modifier le mapping' : 'Nouveau mapping'}
        onClose={() => setModalOpen(false)}
        contentAs="form"
        contentProps={{ onSubmit: handleSave }}
        footer={(
          <>
            <AppButton variant="secondary" onClick={() => setModalOpen(false)}>Annuler</AppButton>
            <AppButton type="submit" variant="success" loading={saving} loadingLabel="Enregistrement…">
              Enregistrer
            </AppButton>
          </>
        )}
      >
        <AppFormField label="Product ID">
          <AppInput
            type="number"
            value={form.productId}
            onChange={(e) => setForm((f) => ({ ...f, productId: e.target.value }))}
          />
        </AppFormField>
        <AppFormField label="Référence">
          <AppInput
            type="text"
            value={form.productRef}
            onChange={(e) => setForm((f) => ({ ...f, productRef: e.target.value }))}
          />
        </AppFormField>
        <AppFormField label="Label">
          <AppInput
            type="text"
            value={form.productLabel}
            onChange={(e) => setForm((f) => ({ ...f, productLabel: e.target.value }))}
          />
        </AppFormField>
        <AppFormField label="Société" required>
          <AppSelect
            required
            value={form.societe}
            onChange={(e) => setForm((f) => ({ ...f, societe: e.target.value }))}
          >
            {SOCIETES.map((s) => <option key={s} value={s}>{s}</option>)}
          </AppSelect>
        </AppFormField>
        <AppFormField label="Famille">
          <AppInput
            type="text"
            value={form.famille}
            onChange={(e) => setForm((f) => ({ ...f, famille: e.target.value }))}
          />
        </AppFormField>
      </AppModal>
      {modal}
    </MasterLayout>
  );
};

export default SecteursAdminProduitsSocietePage;
